import { Icon } from "@material-ui/core";
import { closeMenu, openMenu, toggleMenu, useTopMenuState } from "./slice";
import { useDispatch } from "react-redux";
import StyledMenuItem from "./components/StyledMenuItem";
import TopMenuLabel from "./components/SubmenuLabel";
import { forwardRef, useRef, } from "react";
import StyledMenu from "./components/StyledMenu";
import { parseIcon } from "./utils/iconMapping";
import TypedIcon from "../common/TypedIcon";
const hoverDelayBeforeOpen = 500;
const RenderTopMenu = forwardRef(({ breakLevel, last, level, menuItem, siblings, ...props }, ref) => {
    const dispatch = useDispatch();
    const timeout = useRef(0);
    const hovering = useRef(false);
    const position = useRef([]);
    const { openKeys } = useTopMenuState();
    const offsetIcon = !menuItem.Icon && siblings.find((x) => x.Icon);
    const key = `${level}-${menuItem.ID}`;
    const open = openKeys.includes(key);
    const toggle = () => dispatch(toggleMenu(key));
    const sameLevelOpenSubmenu = openKeys.find((key) => key.startsWith(level + ""));
    const countTotal = (selector, base = 0) => position.current.reduce((total, current) => total + selector(current), base);
    const handleMouseMove = (event) => {
        const deltaX = event.movementX;
        const deltaY = event.movementY;
        const distanceX = countTotal((x) => x.deltaX, deltaX);
        const distanceY = countTotal((x) => x.deltaY, deltaY);
        position.current.push({ deltaX, deltaY, distanceX, distanceY });
        const verticalMovement = position.current.length > 3 &&
            Math.abs(distanceY) / 2 > Math.abs(distanceX);
        const openDirectly = !sameLevelOpenSubmenu || verticalMovement;
        if (openDirectly) {
            dispatch(openMenu(key));
        }
        else {
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => {
                if (hovering.current) {
                    dispatch(openMenu(key));
                }
            }, hoverDelayBeforeOpen);
        }
    };
    const handleSubmenuMouseEnter = () => {
        hovering.current = true;
    };
    const handleMouseLeave = () => {
        hovering.current = false;
        position.current = [];
    };
    const closeMenuWithSameLevel = () => {
        if (sameLevelOpenSubmenu) {
            dispatch(closeMenu(sameLevelOpenSubmenu));
        }
    };
    const closeAllMenus = () => dispatch(closeMenu(""));
    const handleMenuItemMouseEnter = () => {
        hovering.current = true;
        setTimeout(() => {
            if (hovering.current) {
                closeMenuWithSameLevel();
            }
        }, hoverDelayBeforeOpen);
    };
    const handleLinkClick = () => {
        const url = menuItem.Url.substr(3);
        window.RootAPI.Navigation.openContentUrl(url, menuItem.Title);
        closeAllMenus();
    };
    const handleSubmenuClick = (event) => {
        toggle();
        event.stopPropagation();
    };
    const icon = parseIcon(menuItem.Icon || "");
    const label = (<TopMenuLabel style={{ paddingLeft: offsetIcon ? 35 : undefined }} component="span">
        {icon && <TypedIcon {...icon}>{icon.icon}</TypedIcon>}
        {menuItem.Title}
      </TopMenuLabel>);
    const style = {
        backgroundColor: open ? "rgba(255, 255, 255, 0.15)" : undefined,
        borderBlockWidth: last ? 0 : undefined,
    };
    const reverseStackingSideNext = breakLevel <= level;
    const handleKeyDown = (event) => {
        const cancelDefault = () => {
            event.preventDefault();
            event.stopPropagation();
        };
        const target = event.target;
        const prev = target.previousElementSibling;
        const next = target.nextElementSibling;
        const parent = target.parentElement;
        const first = parent.firstElementChild;
        const last = parent.lastElementChild;
        const open = () => {
            const isSubtree = menuItem.SubMenuItems.find((x) => x.ID === target.id)?.HasChilds;
            if (isSubtree) {
                target.click();
            }
        };
        switch (event.code) {
            case "ArrowDown":
                if (next) {
                    next.focus();
                }
                else {
                    first.focus();
                }
                cancelDefault();
                break;
            case "ArrowUp":
                if (prev) {
                    prev.focus();
                }
                else {
                    last.focus();
                }
                cancelDefault();
                break;
            case "ArrowLeft":
            case "ArrowRight":
                if (event.code === "ArrowRight") {
                    open();
                }
                else {
                    toggle();
                }
                cancelDefault();
                break;
            case "Enter":
                open();
                cancelDefault();
                break;
            case "Escape":
                toggle();
                cancelDefault();
                break;
        }
    };
    const menuItemProps = {
        id: menuItem.ID,
        ref,
        style,
        key,
        onMouseLeave: handleMouseLeave,
        onMouseMove: handleMouseMove,
        ...props,
    };
    return menuItem.SubMenuItems ? (<>
        <StyledMenuItem onMouseEnter={handleSubmenuMouseEnter} onClick={handleSubmenuClick} {...menuItemProps}>
          {label}
          <Icon>chevron_right</Icon>
        </StyledMenuItem>
        <StyledMenu anchorEl={() => document.getElementById(menuItem.ID)} reverseStackingSide={reverseStackingSideNext} open={open} onClose={closeAllMenus} MenuListProps={{ onKeyDown: handleKeyDown }} PaperProps={{
            onFocus: ({ target }) => {
                const paperFocused = target.className.includes("MuiPopover-paper");
                if (paperFocused) {
                    const firstElement = target.firstElementChild
                        ?.firstElementChild;
                    firstElement?.focus();
                }
            },
        }}>
          {menuItem.SubMenuItems.map((subItem, index) => (<RenderTopMenu key={subItem.ID} menuItem={subItem} level={level + 1} breakLevel={breakLevel} last={index + 1 === menuItem.SubMenuItems.length} siblings={menuItem.SubMenuItems}/>))}
        </StyledMenu>
      </>) : (<StyledMenuItem onClick={handleLinkClick} onMouseEnter={handleMenuItemMouseEnter} {...menuItemProps}>
        {label}
      </StyledMenuItem>);
});
export default RenderTopMenu;
